import { f0FormField, F0Form } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import { useMultiLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { useMultiLocationSelectorFormField } from 'modules/core/lib/formFields/locationSelector'
import { useMultiTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'
import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'

import { getParticipantsLabel } from 'modules/engagement_surveys/lib/participantStrategyLabels'
import { memo, useMemo } from 'react'

const RECURRENCE_VALUES = ['onetime', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const

const STRATEGY_VALUES = [
  'everyone',
  'all_employees',
  'employees',
  'managers',
  'teams',
  'locations',
  'legal_entities',
] as const

type UseSurveySettingsSchemaParams = {
  disablePublishingFields?: boolean
}

const useSurveySettingsSchema = ({
  disablePublishingFields = false,
}: UseSurveySettingsSchemaParams) => {
  const multiLocationSelectorFormField = useMultiLocationSelectorFormField({
    label: i18n.t('surveys.configuration.participants.locations_label'),
    helpText: i18n.t('surveys.configuration.participants.locations_help_text'),
    section: 'participants',
    mandatory: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'locations',
    },
  })

  const multiTeamSelectorFormField = useMultiTeamSelectorFormField({
    label: i18n.t('surveys.configuration.participants.teams_label'),
    helpText: i18n.t('surveys.configuration.participants.teams_help_text'),
    section: 'participants',
    mandatory: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'teams',
    },
  })

  const multiLegalEntitySelectorFormField = useMultiLegalEntitySelectorFormField({
    label: i18n.t('surveys.configuration.participants.legal_entities_label'),
    helpText: i18n.t('surveys.configuration.participants.legal_entities_help_text'),
    section: 'participants',
    mandatory: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'legal_entities',
    },
  })

  const editorIdsFormField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('surveys.configuration.editors.label'),
        section: 'editors',
        mandatory: true,
      }),
    []
  )

  const participantsFormField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('surveys.configuration.participants.employees_label'),
        helpText: i18n.t('surveys.configuration.participants.employees_help_text'),
        section: 'participants',
        mandatory: true,
        renderIf: {
          fieldId: 'participantStrategy',
          equalsTo: 'employees',
        },
      }),
    []
  )

  return z
    .object({
      title: f0FormField(z.string().min(1), {
        label: i18n.t('surveys.configuration.basic_info.name'),
        section: 'basic-info',
      }),
      description: f0FormField(z.string().optional(), {
        label: i18n.t('surveys.configuration.basic_info.description'),
        section: 'basic-info',
        fieldType: 'textarea',
        rows: 3,
      }),
      participantStrategy: f0FormField(z.enum(STRATEGY_VALUES), {
        label: i18n.t('surveys.configuration.participants.label'),
        section: 'participants',
        options: STRATEGY_VALUES.map((value) => ({
          value,
          label: getParticipantsLabel(value),
        })),
      }),
      participants: participantsFormField,
      legalEntities: multiLegalEntitySelectorFormField,
      locations: multiLocationSelectorFormField,
      teams: multiTeamSelectorFormField,
      startDate: f0FormField(
        disablePublishingFields ? z.date().optional() : z.date().min(new Date()).optional(),
        {
          label: i18n.t('forms.schedule.start_date'),
          fieldType: 'datetime',
          section: 'schedule',
          disabled: disablePublishingFields,
          minDate: !disablePublishingFields ? new Date() : undefined,
          maxDate: ({ values }: { values: Record<string, unknown> }) =>
            !disablePublishingFields && values ? new Date(`${values.endsAt}`) : undefined,
          renderIf: ({ values }: { values: Record<string, unknown> }) =>
            disablePublishingFields ? !!values.startDate : true,
        }
      ),
      endsAt: f0FormField(z.date().optional(), {
        label: i18n.t('forms.schedule.end_date'),
        fieldType: 'datetime',
        section: 'schedule',
        minDate: ({ values }: { values: Record<string, unknown> }) => {
          if (disablePublishingFields) return undefined

          if (values.startDate) return new Date(`${values.startDate}`)

          return new Date()
        },
        disabled: disablePublishingFields,
        renderIf: ({ values }: { values: Record<string, unknown> }) =>
          disablePublishingFields ? !!values.endsAt : true,
      }),
      recurrenceType: f0FormField(z.enum(RECURRENCE_VALUES), {
        label: i18n.t('forms.schedule.recurrence'),
        section: 'schedule',
        options: [
          { value: 'onetime', label: i18n.t('forms.schedule.recurrence_options.onetime') },
          { value: 'daily', label: i18n.t('forms.schedule.recurrence_options.daily') },
          { value: 'weekly', label: i18n.t('forms.schedule.recurrence_options.weekly') },
          { value: 'monthly', label: i18n.t('forms.schedule.recurrence_options.monthly') },
          { value: 'quarterly', label: i18n.t('forms.schedule.recurrence_options.quarterly') },
          { value: 'yearly', label: i18n.t('forms.schedule.recurrence_options.yearly') },
        ],
        placeholder: i18n.t('forms.schedule.recurrence_options.onetime'),
        disabled: disablePublishingFields,
      }),
      daysToAnswer: f0FormField(z.number().optional(), {
        label: i18n.t('forms.schedule.days_to_answer'),
        section: 'schedule',
        fieldType: 'number',
        renderIf: {
          fieldId: 'recurrenceType',
          notIncludes: 'onetime',
        },
        disabled: disablePublishingFields,
      }),
      restrictedViewersEnabled: f0FormField(z.boolean(), {
        label: i18n.t('surveys.configuration.visibility.restricted_viewers.label'),
        helpText: i18n.t('surveys.configuration.visibility.restricted_viewers.description'),
        section: 'visibility',
        fieldType: 'switch',
      }),
      anonymous: f0FormField(z.boolean(), {
        label: i18n.t('surveys.configuration.privacy_settings.anonymous_answers'),
        helpText: i18n.t('surveys.configuration.privacy_settings.anonymous_answers_description'),
        section: 'visibility',
        fieldType: 'switch',
      }),
      editorIds: editorIdsFormField,
    })
    .superRefine((data, ctx) => {
      // Validate end date is on or after start date
      if (data.startDate && data.endsAt && data.endsAt < data.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('forms.schedule.end_date_must_be_after_start_date'),
          path: ['endsAt'],
        })
      }

      if (data.startDate && data.endsAt && data.startDate > data.endsAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('forms.schedule.start_date_must_be_before_end_date'),
          path: ['startDate'],
        })
      }
    })
}

export type SurveySettingsFormValues = z.infer<ReturnType<typeof useSurveySettingsSchema>>

const styling = {
  showSectionsSidepanel: false,
}

type SurveySettingsFormProps = {
  defaultValues: Partial<SurveySettingsFormValues>
  onSubmit: (
    values: SurveySettingsFormValues
  ) => Promise<{ success: boolean; rootMessage?: string }>
  disablePublishingFields?: boolean
}

const SurveySettingsFormComponent = ({
  disablePublishingFields = false,
  defaultValues,
  onSubmit,
}: SurveySettingsFormProps) => {
  const surveySettingsSchema = useSurveySettingsSchema({ disablePublishingFields })

  const submitConfig = useMemo(
    () => ({
      type: 'action-bar' as const,
      label: i18n.t('common.save'),
      discardable: true,
      centerActionBarInFrameContent: true,
    }),
    []
  )

  const sections = useMemo(
    () => ({
      'basic-info': {
        title: i18n.t('surveys.configuration.basic_info.title'),
      },
      participants: {
        title: i18n.t('surveys.configuration.participants.title'),
      },
      schedule: {
        title: i18n.t('surveys.configuration.publish_settings.title'),
      },
      visibility: {
        title: i18n.t('surveys.configuration.visibility.title'),
      },
      editors: {
        title: i18n.t('surveys.configuration.editors.title'),
      },
    }),
    []
  )

  return (
    <F0Form
      name="survey-settings"
      schema={surveySettingsSchema}
      sections={sections}
      defaultValues={defaultValues}
      submitConfig={submitConfig}
      onSubmit={onSubmit}
      styling={styling}
    />
  )
}

export const SurveySettingsForm = memo(SurveySettingsFormComponent)
